import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { MaterialModule } from 'src/app/material.module';
import { BaseRespone } from 'src/app/models/common.response';
import { AbhaService } from '../abha.service';
import { AbhaCardResult } from '../iabha-info';

const FILE_EXTENSIONS: Readonly<Record<string, string>> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'application/pdf': 'pdf',
};

// View and download the ABHA card ABDM renders (NHA M1 CRT_ABHA_114).
@Component({
  selector: 'app-abha-card',
  standalone: true,
  imports: [CommonModule, MaterialModule, TranslateModule],
  templateUrl: './abha-card.component.html',
  styleUrl: './abha-card.component.scss',
})
export class AbhaCardComponent implements OnInit {
  @Input({ required: true }) cardToken!: string;
  @Input() abhaNumber = '';
  @Input() autoDownload = false;

  cardSource = '';
  isImage = false;
  isLoading = false;
  fileName = 'abha-card';
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private abhaService: AbhaService,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    if (this.autoDownload) {
      this.onViewCard();
    }
  }

  onViewCard(): void {
    this.isLoading = true;
    this.abhaService
      .getCard(this.cardToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => this.handleCardResponse(response));
  }

  private handleCardResponse(response: BaseRespone<AbhaCardResult>): void {
    this.isLoading = false;
    if (response && response.isSuccess && response.result?.contentBase64) {
      this.showCard(response.result);
      return;
    }

    const error = response?.errors?.length ? response.errors.join('; ') : response?.message;
    this.toastr.error(error || this.translate.instant('ABHA.CARD_FAILED'));
  }

  private showCard(card: AbhaCardResult): void {
    const extension = FILE_EXTENSIONS[card.contentType] || 'png';
    this.cardSource = `data:${card.contentType};base64,${card.contentBase64}`;
    this.isImage = card.contentType.startsWith('image/');
    this.fileName = `abha-card-${this.abhaNumber.replace(/[^0-9]/g, '')}.${extension}`;
    if (this.autoDownload) {
      const link = document.createElement('a');
      link.href = this.cardSource;
      link.download = this.fileName;
      link.click();
    }
  }
}
